<template>
    <div id="general-ledger" class="w-full h-full flex flex-col p-3">
        <div class="flex items-center gap-2">
            <gen-leg-filter v-model="query"/>
            <UButton
                size="sm"
                variant="outline"
                color="neutral"
                @click="exportCSV"
            >
                Eksport CSV
            </UButton>
            <UButton
                size="sm"
                variant="outline"
                color="neutral"
                @click="showGenLegReportExportsDialog"
            >
                Eksport...
            </UButton>
        </div>
        <loading-bar :style="{visibility: loading ? 'visible' : 'hidden'}" data-cy="loading-indicator"/>
        <div class="flex flex-col gap-2 overflow-y-auto bg-base-200">
            <dynamic-scroller
                :items="ledger?.ledgerAccounts || []"
                :min-item-size="200"
                key-field="accountNo"
                class="h-full"
            >
                <template #default="{item, index, active}">
                    <dynamic-scroller-item
                        :item="item"
                        :active="active"
                        :data-index="index"
                        :size-dependencies="[item.numTransactionLines]"
                    >
                        <div v-if="active" :style="{height: calcLedgerHeight(item)+'px'}" class="py-1.5">
                            <gen-leg-account-grid
                                :account-no="item.accountNo"
                                :ledger="item"
                                :active="active"
                                :query="query"
                            />
                        </div>
                    </dynamic-scroller-item>
                </template>
            </dynamic-scroller>
        </div>
        <div v-if="!ledger?.ledgerAccounts?.length && currentAccountNo" class="mt-4">
            Ingen saldo for valgt konto i perioden.
        </div>
    </div>
</template>

<script setup lang="ts">
//@ts-ignore
import {DynamicScroller, DynamicScrollerItem} from 'vue-virtual-scroller'
import {hasSelectedOrg, orgSelectionComplete} from '@/tenants/org.service'
import {LedgerAccount} from '@/types/reports/LedgerAccount'
import _, {clamp, noop} from 'lodash'
import {useEmitter} from '@/services/infrastructure/emitter'
import {ORG_SELECTED} from '@/types/events'
import {usePanelManager} from '@tritt/multipanel'
import {DELETE_TRANSACTION, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import LoadingBar from '@/components/LoadingBar.vue'
import {getGeneralLedger} from '@/genleg/ledger.service'
import {createGeneralLedgerCSVExport} from '@/services/reports/summaryReport.service'
import {download} from '@/services/infrastructure/storage.service'
import {showExportsDialog} from '@/exports/exports.service'
import {ReportType} from '@/types/reports/Report'
import {Ledger} from '@/types/reports/Ledger'
import {GENERAL_LEDGER} from '@/genleg'
import {type GenLegQuery} from '@/genleg/GenLegQuery'
import {getPersistedRangeOr} from '@/dateUtils/dateRange.service'
import {format, startOfYear, endOfYear} from 'date-fns'
import {Ref} from 'vue/dist/vue'
import GenLegAccountGrid from '@/genleg/GenLegAccountGrid.vue'
import GenLegFilter from '@/genleg/GenLegFilter.vue'
import {computed, inject, onMounted, onUnmounted, ref, watch} from 'vue'
import {PanelContentRequest} from '@tritt/multipanel'

const props = defineProps({
    accountNo: {type: String, required: false},
    fromDate: {type: String, required: false},
    toDate: {type: String, required: false},
    fromAccountNo: {type: String, required: false},
    toAccountNo: {type: String, required: false},
})

// ── Query (single source of truth) ───────────────────────────────────────────

const buildQuery = (): GenLegQuery => {
    const persisted = getPersistedRangeOr({start: startOfYear(new Date()), end: endOfYear(new Date())})
    const fromDate = props.fromDate ?? format(persisted.start, 'yyyy-MM-dd')
    const toDate = props.toDate ?? format(persisted.end, 'yyyy-MM-dd')

    const query: GenLegQuery = {fromDate, toDate}

    if (props.accountNo) {
        query.accountNo = props.accountNo
    } else if (props.fromAccountNo || props.toAccountNo) {
        if (props.fromAccountNo) query.fromAccountNo = props.fromAccountNo
        if (props.toAccountNo) query.toAccountNo = props.toAccountNo
    }

    return query
}

const query = ref<GenLegQuery>(buildQuery())

// Reset query when drill-down props change
watch(
    () => [props.accountNo, props.fromDate, props.toDate, props.fromAccountNo, props.toAccountNo],
    () => { query.value = buildQuery() },
)

// ── Data fetching ─────────────────────────────────────────────────────────────

const ledger = ref<Ledger>()
const loading = ref(false)
const panelId: Ref<number> | undefined = inject('panelId')

const {onEvents, cleanUp} = useEmitter()
const {setPanelPath} = usePanelManager()

const fetchLedger = async () => {
    loading.value = true
    ledger.value = await getGeneralLedger(query.value)
    setTimeout(() => {loading.value = false}, 100)
}

watch(query, fetchLedger, {deep: true})

const debouncedRefresh = _.debounce(fetchLedger, 500, {leading: false, trailing: true})

onMounted(async () => {
    await orgSelectionComplete
    if (!hasSelectedOrg()) {
        console.error('Accessing the ledger page without selected org. Should not be possible, this is a bug!')
    }
    await fetchLedger()
    onEvents([TRANSACTION_CREATED, TRANSACTION_UPDATED, DELETE_TRANSACTION, ORG_SELECTED], debouncedRefresh)
})

onUnmounted(cleanUp)

// ── Panel path ────────────────────────────────────────────────────────────────

const currentAccountNo = computed(() => query.value.accountNo)

watch(currentAccountNo, (no) => {
    if (no !== props.accountNo) {
        const payload: PanelContentRequest = {
            componentName: GENERAL_LEDGER.componentName,
            queryValues: {},
            panelId: panelId?.value,
        }
        setPanelPath(payload)
    }
})

// ── Exports ───────────────────────────────────────────────────────────────────

const exportCSV = async () => {
    const {fromDate, toDate, project, department, fullText} = query.value
    const csv = await createGeneralLedgerCSVExport({fromDate, toDate, project, department, query: fullText})
    if (csv) download(csv).then(noop)
}

const showGenLegReportExportsDialog = () => {
    const {fromDate, toDate} = query.value
    showExportsDialog(ReportType.GENLEG, {fromDate, toDate})
}

// ── Layout ────────────────────────────────────────────────────────────────────

const calcLedgerHeight = (ledgerAccount: LedgerAccount) => {
    const cappedlineCount = clamp(ledgerAccount.numTransactionLines, 0, 20)
    return cappedlineCount * 29 + 180
}

// @ts-ignore
window.pdfExport = () => {
    if (!ledger.value) return console.error('Ledger is not loaded')
}
</script>

<style>
@reference "@/index.css";
@reference "tailwindcss";

#general-ledger {
    .ag-header {
        height: 0;
        border: 0;
    }
}
</style>
